import type { PunishmentStep } from '../types';

interface PunishmentStepEditorProps {
  punishments: PunishmentStep[];
  onChange: (punishments: PunishmentStep[]) => void;
}

const PUNISHMENT_TYPES = [
  { value: 'warn', label: '경고' },
  { value: 'timeout', label: '타임아웃' },
  { value: 'kick', label: '추방' },
  { value: 'ban', label: '차단' },
] as const;

export default function PunishmentStepEditor({ punishments, onChange }: PunishmentStepEditorProps) {
  const handleAddStep = () => {
    const newStep: PunishmentStep = {
      step: punishments.length + 1,
      type: 'warn',
    };
    onChange([...punishments, newStep]);
  };

  const handleRemoveStep = (index: number) => {
    const newPunishments = punishments.filter((_, i) => i !== index);
    // 단계 번호 재정렬
    const reorderedPunishments = newPunishments.map((p, i) => ({
      ...p,
      step: i + 1,
    }));
    onChange(reorderedPunishments);
  };

  const handleUpdateStep = (index: number, field: keyof PunishmentStep, value: any) => {
    const newPunishments = [...punishments];
    newPunishments[index] = {
      ...newPunishments[index],
      [field]: value,
    };
    onChange(newPunishments);
  };

  return (
    <div className="space-y-3">
      {punishments.map((punishment, index) => (
        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] rounded-full flex items-center justify-center text-white font-bold text-sm">
            {punishment.step}
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-center space-x-2">
              <select
                value={punishment.type}
                onChange={(e) => handleUpdateStep(index, 'type', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PUNISHMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => handleRemoveStep(index)}
                className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                삭제
              </button>
            </div>

            {(punishment.type === 'timeout' || punishment.type === 'ban') && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  기간 (초)
                </label>
                <input
                  type="number"
                  value={punishment.duration || ''}
                  onChange={(e) => handleUpdateStep(index, 'duration', parseInt(e.target.value) || undefined)}
                  placeholder={punishment.type === 'timeout' ? '예: 3600 (1시간)' : '예: 86400 (1일)'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-600 mb-1">
                사유 (선택사항)
              </label>
              <input
                type="text"
                value={punishment.reason || ''}
                onChange={(e) => handleUpdateStep(index, 'reason', e.target.value || undefined)}
                placeholder="제재 사유를 입력하세요"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={handleAddStep}
        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
      >
        + 제재 단계 추가
      </button>
    </div>
  );
}
